import React, { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, Loader2, Shield, User as UserIcon, X } from "lucide-react";
import client from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const ROLES = ["admin", "customer"];

const UserForm = ({ initial, onClose, onSaved }) => {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    name: initial?.name || "",
    email: initial?.email || "",
    password: "",
    role: initial?.role || "admin",
    company: initial?.company || "",
    phone: initial?.phone || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try {
      if (isEdit) {
        const body = { name: form.name, email: form.email, role: form.role, company: form.company, phone: form.phone };
        if (form.password) body.password = form.password;
        const { data } = await client.patch(`/admin/users/${initial.id}`, body);
        toast.success("User updated");
        onSaved(data);
      } else {
        if (!form.password) { toast.error("Password is required"); return; }
        const { data } = await client.post("/admin/users", form);
        toast.success("User created");
        onSaved(data);
      }
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save user");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit User" : "Create User"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 pt-1">
          <div>
            <label className="text-xs text-steel-light uppercase tracking-wider">Full Name *</label>
            <Input className="mt-1" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Jane Smith" />
          </div>
          <div>
            <label className="text-xs text-steel-light uppercase tracking-wider">Email *</label>
            <Input type="email" className="mt-1" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="jane@gotparts.ca" />
          </div>
          <div>
            <label className="text-xs text-steel-light uppercase tracking-wider">{isEdit ? "New Password (leave blank to keep current)" : "Password *"}</label>
            <Input type="password" className="mt-1" value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="Min. 6 characters" />
          </div>
          <div>
            <label className="text-xs text-steel-light uppercase tracking-wider">Role *</label>
            <select value={form.role} onChange={(e) => set("role", e.target.value)} className="mt-1 w-full h-10 px-3 border border-border rounded-md text-sm outline-none focus:border-orange bg-white">
              {ROLES.map((r) => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-steel-light uppercase tracking-wider">Company</label>
              <Input className="mt-1" value={form.company} onChange={(e) => set("company", e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-steel-light uppercase tracking-wider">Phone</label>
              <Input className="mt-1" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-border">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={submit} disabled={saving || !form.name || !form.email} className="bg-orange hover:bg-orange-hover text-white">
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              {isEdit ? "Save Changes" : "Create User"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Users = () => {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formTarget, setFormTarget] = useState(null); // null=closed, false=create, obj=edit
  const [deleting, setDeleting] = useState(null);
  const [roleFilter, setRoleFilter] = useState("all");

  const load = useCallback(() => {
    setLoading(true);
    const params = roleFilter !== "all" ? { role: roleFilter } : {};
    client.get("/admin/users", { params }).then((r) => setItems(r.data.items)).finally(() => setLoading(false));
  }, [roleFilter]);

  useEffect(() => { load(); }, [load]);

  const onSaved = (user) => {
    setItems((prev) => {
      const idx = prev.findIndex((u) => u.id === user.id);
      if (idx >= 0) { const next = [...prev]; next[idx] = user; return next; }
      return [user, ...prev];
    });
  };

  const confirmDelete = async (u) => {
    setDeleting(u.id);
    try {
      await client.delete(`/admin/users/${u.id}`);
      setItems((prev) => prev.filter((x) => x.id !== u.id));
      toast.success(`${u.name} deleted`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete user");
    } finally { setDeleting(null); }
  };

  const visible = items.filter((u) => (roleFilter === "all" || u.role === roleFilter));

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-2xl font-bold tracking-tight text-graphite">Users & Admins</h1>
        <Button onClick={() => setFormTarget(false)} className="bg-orange hover:bg-orange-hover text-white h-9">
          <Plus className="w-4 h-4 mr-1.5" /> New User
        </Button>
      </div>
      <p className="text-sm text-steel-light mt-1 mb-5">Manage admin accounts and all registered users.</p>

      <div className="flex gap-2 mb-4">
        {["all", "admin", "customer"].map((r) => (
          <button key={r} onClick={() => setRoleFilter(r)} className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${roleFilter === r ? "bg-graphite text-white border-graphite" : "border-border text-steel hover:border-graphite"}`}>
            {r === "all" ? "All" : r.charAt(0).toUpperCase() + r.slice(1) + "s"}
          </button>
        ))}
      </div>

      <div className="border border-border rounded-lg overflow-hidden bg-white">
        <table className="w-full text-sm">
          <thead className="bg-surface">
            <tr className="text-left mono text-xs uppercase tracking-wider text-steel-light">
              <th className="px-4 py-2.5">Name</th>
              <th className="px-4 py-2.5">Email</th>
              <th className="px-4 py-2.5">Role</th>
              <th className="px-4 py-2.5">Company</th>
              <th className="px-4 py-2.5">Created</th>
              <th className="px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="py-12 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-steel-light" /></td></tr>}
            {!loading && visible.length === 0 && <tr><td colSpan={6} className="py-12 text-center text-steel-light text-sm">No users found.</td></tr>}
            {!loading && visible.map((u) => (
              <tr key={u.id || u.email} className="border-t border-border hover:bg-surface/50">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className={`w-7 h-7 rounded-full grid place-items-center text-xs font-semibold shrink-0 ${u.role === "admin" ? "bg-orange/15 text-orange" : "bg-surface border border-border text-steel"}`}>
                      {u.role === "admin" ? <Shield className="w-3.5 h-3.5" /> : (u.name || "?").charAt(0).toUpperCase()}
                    </div>
                    <span className="font-medium text-graphite">{u.name || "—"}</span>
                    {u.email === me?.email && <span className="mono text-[10px] bg-orange/10 text-orange rounded px-1">you</span>}
                  </div>
                </td>
                <td className="px-4 py-3 text-steel">{u.email}</td>
                <td className="px-4 py-3">
                  <span className={`mono text-xs px-2 py-0.5 rounded border ${u.role === "admin" ? "bg-orange/5 border-orange/20 text-orange" : "bg-surface border-border text-steel-light"}`}>{u.role}</span>
                </td>
                <td className="px-4 py-3 text-steel">{u.company || "—"}</td>
                <td className="px-4 py-3 mono text-xs text-steel-light">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <button onClick={() => setFormTarget(u)} className="p-1.5 rounded text-steel-light hover:text-orange hover:bg-surface"><Pencil className="w-4 h-4" /></button>
                    {u.email !== me?.email && (
                      <button onClick={() => confirmDelete(u)} disabled={deleting === u.id} className="p-1.5 rounded text-steel-light hover:text-red-600 hover:bg-red-50">
                        {deleting === u.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {formTarget !== null && (
        <UserForm
          initial={formTarget || null}
          onClose={() => setFormTarget(null)}
          onSaved={onSaved}
        />
      )}
    </div>
  );
};

export default Users;
